import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Mail, Send, Settings as SettingsIcon, Unplug } from 'lucide-react';
import AlertMessage from '../components/common/AlertMessage';
import { useAuth } from '../contexts/AuthContext';
import { apiService, type TelegramSettingsData, type UserSettings } from '../services/api';

const LINK_STATUS_POLL_INTERVAL_MS = 5000;

const DEFAULT_TELEGRAM_SETTINGS: TelegramSettingsData = {
  linked: false,
  enabled: false,
  linkStatus: 'not_connected',
};

const normalizeTelegramSettings = (value: unknown): TelegramSettingsData => {
  if (!value || typeof value !== 'object') {
    return { ...DEFAULT_TELEGRAM_SETTINGS };
  }

  const telegram = value as Record<string, unknown>;
  return {
    linked: Boolean(telegram.linked),
    enabled: Boolean(telegram.enabled),
    username: typeof telegram.username === 'string' ? telegram.username : undefined,
    linkedAt: typeof telegram.linkedAt === 'string' ? telegram.linkedAt : undefined,
    linkStatus: typeof telegram.linkStatus === 'string' ? telegram.linkStatus : undefined,
  };
};

const normalizeSettingsDocument = (value: unknown): UserSettings | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const document = value as Record<string, unknown>;
  const idValue = typeof document.id === 'string'
    ? document.id
    : typeof document._id === 'string'
      ? document._id
      : '';

  if (!idValue) {
    return null;
  }

  return {
    id: idValue,
    version: typeof document.version === 'number' ? document.version : 0,
    telegram: normalizeTelegramSettings(document.telegram),
  };
};

const createInitialSettingsPayload = (): { telegram: TelegramSettingsData } => ({
  telegram: { ...DEFAULT_TELEGRAM_SETTINGS },
});

const deriveTelegramStatus = (
  telegram: TelegramSettingsData,
  linkExpiresAt: string | null,
  hasTemporaryError: boolean
): string => {
  if (hasTemporaryError) {
    return 'Temporary error';
  }

  if (telegram.linked && telegram.enabled) {
    return 'Connected';
  }

  if (telegram.linked && !telegram.enabled) {
    return 'Connected but disabled';
  }

  if (linkExpiresAt) {
    const expiresAt = new Date(linkExpiresAt).getTime();
    if (!Number.isNaN(expiresAt) && expiresAt > Date.now()) {
      return 'Linking in progress';
    }
    return 'Link expired';
  }

  return 'Not connected';
};

const formatLinkedAt = (linkedAt?: string): string | null => {
  if (!linkedAt) {
    return null;
  }

  const date = new Date(linkedAt);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const normalizeTelegramBotUrl = (rawUrl: string): string => {
  const trimmed = rawUrl.trim();
  if (!trimmed) {
    return trimmed;
  }

  try {
    const parsed = new URL(trimmed);
    if (parsed.hostname === 't.me' && parsed.pathname.startsWith('/@')) {
      parsed.pathname = parsed.pathname.replace('/@', '/');
      return parsed.toString();
    }
    return parsed.toString();
  } catch {
    return trimmed.replace('t.me/@', 't.me/');
  }
};

const getTelegramStatusTone = (status: string): string => {
  switch (status) {
    case 'Connected':
      return 'border-emerald-400/40 bg-emerald-500/15 text-emerald-100';
    case 'Connected but disabled':
      return 'border-amber-400/40 bg-amber-500/15 text-amber-100';
    case 'Linking in progress':
      return 'border-sky-400/40 bg-sky-500/15 text-sky-100';
    case 'Link expired':
    case 'Temporary error':
      return 'border-rose-400/40 bg-rose-500/15 text-rose-100';
    default:
      return 'border-gray-600 bg-gray-800 text-gray-100';
  }
};

const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordErrorMessage, setPasswordErrorMessage] = useState<string | null>(null);
  const [passwordSuccessMessage, setPasswordSuccessMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [settingsDocument, setSettingsDocument] = useState<UserSettings | null>(null);
  const [isSettingsLoading, setIsSettingsLoading] = useState(true);
  const [telegramErrorMessage, setTelegramErrorMessage] = useState<string | null>(null);
  const [telegramSuccessMessage, setTelegramSuccessMessage] = useState<string | null>(null);
  const [isTelegramBusy, setIsTelegramBusy] = useState(false);
  const [linkExpiresAt, setLinkExpiresAt] = useState<string | null>(null);
  const [isTelegramServiceConfigured, setIsTelegramServiceConfigured] = useState(true);
  const [pendingTelegramBotUrl, setPendingTelegramBotUrl] = useState<string | null>(null);

  const loadTelegramServiceStatus = async () => {
    try {
      const status = await apiService.getTelegramServiceStatus();
      const configured = Boolean(status?.configured);
      setIsTelegramServiceConfigured(configured);
      if (!configured && !telegramErrorMessage) {
        setTelegramErrorMessage(
          status?.message ||
            'Telegram bot is not configured. Ask the administrator to set TELEGRAM_BOT_TOKEN and TELEGRAM_BOT_USERNAME.'
        );
      }
    } catch {
      setIsTelegramServiceConfigured(false);
      if (!telegramErrorMessage) {
        setTelegramErrorMessage('Telegram service is currently unavailable. Please try again later.');
      }
    }
  };

  const loadSettings = async (showLoader: boolean = true) => {
    if (!user?.id) {
      if (showLoader) {
        setIsSettingsLoading(false);
      }
      return;
    }

    if (showLoader) {
      setIsSettingsLoading(true);
    }

    try {
      const response = await apiService.getUserSettings(user.id);
      const nextSettings = normalizeSettingsDocument(Array.isArray(response) ? response[0] : null);
      setSettingsDocument(nextSettings);
      if (nextSettings?.telegram.linked) {
        setLinkExpiresAt(null);
      }
      setTelegramErrorMessage(null);
    } catch (error) {
      setTelegramErrorMessage(
        error instanceof Error ? error.message : 'Unable to load Telegram settings right now.'
      );
    } finally {
      if (showLoader) {
        setIsSettingsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!user?.id) {
      return;
    }
    void loadSettings();
    void loadTelegramServiceStatus();
  }, [user?.id]);

  useEffect(() => {
    if (!linkExpiresAt || settingsDocument?.telegram.linked) {
      return;
    }

    const expiresAt = new Date(linkExpiresAt).getTime();
    if (Number.isNaN(expiresAt) || expiresAt <= Date.now()) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void loadSettings(false);
    }, LINK_STATUS_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [linkExpiresAt, settingsDocument?.telegram.linked]);

  const ensureSettingsDocument = async (): Promise<UserSettings> => {
    if (settingsDocument) {
      return settingsDocument;
    }

    const created = await apiService.createUserSettings(createInitialSettingsPayload());
    const normalized = normalizeSettingsDocument(created);
    if (!normalized) {
      throw new Error('Settings resource could not be initialized.');
    }

    setSettingsDocument(normalized);
    return normalized;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPasswordErrorMessage(null);
    setPasswordSuccessMessage(null);

    if (!currentPassword.trim() || !newPassword.trim() || !confirmNewPassword.trim()) {
      setPasswordErrorMessage('All password fields are required.');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordErrorMessage('New password must be at least 8 characters long.');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordErrorMessage('New password and confirmation do not match.');
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordErrorMessage('New password must be different from the current password.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiService.changePassword(currentPassword, newPassword);
      setPasswordSuccessMessage(response?.message || 'Password updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error) {
      setPasswordErrorMessage(
        error instanceof Error ? error.message : 'Password update failed. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConnectTelegram = async () => {
    setTelegramErrorMessage(null);
    setTelegramSuccessMessage(null);
    setPendingTelegramBotUrl(null);
    setIsTelegramBusy(true);

    try {
      const status = await apiService.getTelegramServiceStatus();
      if (!status?.configured) {
        setIsTelegramServiceConfigured(false);
        throw new Error(
          status?.message ||
            'Telegram bot is not configured. Ask the administrator to set TELEGRAM_BOT_TOKEN and TELEGRAM_BOT_USERNAME.'
        );
      }
      setIsTelegramServiceConfigured(true);

      await ensureSettingsDocument();
      const response = await apiService.createTelegramLinkToken();
      setLinkExpiresAt(response.expiresAt);
      const botUrl = normalizeTelegramBotUrl(response.botUrl);
      setPendingTelegramBotUrl(botUrl);
      setTelegramSuccessMessage('Telegram link generated. Open the bot with the button below and press Start.');
      await loadSettings(false);
    } catch (error) {
      if (error instanceof Error && error.message) {
        setTelegramErrorMessage(error.message);
      } else {
        setTelegramErrorMessage('Unable to create the Telegram link right now.');
      }
      setPendingTelegramBotUrl(null);
    } finally {
      setIsTelegramBusy(false);
    }
  };

  const handleToggleTelegramNotifications = async (nextEnabled: boolean) => {
    setTelegramErrorMessage(null);
    setTelegramSuccessMessage(null);
    setIsTelegramBusy(true);

    try {
      const currentSettings = await ensureSettingsDocument();
      if (!currentSettings.telegram.linked) {
        throw new Error('Connect Telegram before enabling notifications.');
      }

      const updated = await apiService.updateUserSettings(currentSettings.id, {
        telegram: {
          ...currentSettings.telegram,
          enabled: nextEnabled,
          linkStatus: nextEnabled ? 'connected' : 'connected_disabled',
        },
        version: currentSettings.version,
      });

      const normalized = normalizeSettingsDocument(updated);
      if (!normalized) {
        throw new Error('Telegram settings update returned an invalid payload.');
      }

      setSettingsDocument(normalized);
      setTelegramSuccessMessage(
        nextEnabled
          ? 'Telegram notifications enabled.'
          : 'Telegram notifications disabled. The Telegram account stays connected.'
      );
    } catch (error) {
      setTelegramErrorMessage(
        error instanceof Error ? error.message : 'Unable to update Telegram notifications.'
      );
    } finally {
      setIsTelegramBusy(false);
    }
  };

  const handleDisconnectTelegram = async () => {
    setTelegramErrorMessage(null);
    setTelegramSuccessMessage(null);
    setIsTelegramBusy(true);

    try {
      await apiService.disconnectTelegram();
      setLinkExpiresAt(null);
      setTelegramSuccessMessage('Telegram disconnected successfully.');
      await loadSettings(false);
    } catch (error) {
      setTelegramErrorMessage(
        error instanceof Error ? error.message : 'Unable to disconnect Telegram right now.'
      );
    } finally {
      setIsTelegramBusy(false);
    }
  };

  const telegram = settingsDocument?.telegram ?? DEFAULT_TELEGRAM_SETTINGS;
  const telegramStatus = deriveTelegramStatus(telegram, linkExpiresAt, Boolean(telegramErrorMessage));
  const linkedAtLabel = formatLinkedAt(telegram.linkedAt);
  const telegramToggleDisabled = isSettingsLoading || isTelegramBusy || !telegram.linked || !isTelegramServiceConfigured;
  const telegramStatusTone = getTelegramStatusTone(telegramStatus);

  return (
    <div className="min-h-full bg-gradient-to-b from-gray-900 via-gray-950 to-black py-10 md:py-14">
      <div className="container mx-auto px-4">
        <motion.div
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-gray-700 bg-gray-900/70 px-4 py-2 text-sm text-gray-300">
              <SettingsIcon className="w-4 h-4 text-purple-400" />
              Settings
            </div>
            <h1 className="mt-4 text-3xl md:text-4xl font-bold text-white">Account settings</h1>
            <p className="mt-3 max-w-2xl text-gray-400">
              Manage your account details and keep your password secure. Additional settings can be
              added here over time without changing the main navigation.
            </p>
          </div>

          <div className="rounded-2xl border border-gray-800 bg-gray-900/70 p-6 md:p-8 shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-500/10">
                <Lock className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-white">Account</h2>
                <p className="text-sm text-gray-400">Change your password from your active session.</p>
              </div>
            </div>

            {passwordErrorMessage && (
              <div className="mb-4">
                <AlertMessage variant="error">{passwordErrorMessage}</AlertMessage>
              </div>
            )}

            {passwordSuccessMessage && (
              <div className="mb-4">
                <AlertMessage variant="success">{passwordSuccessMessage}</AlertMessage>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="settings-email" className="mb-1 block text-sm font-medium text-gray-300">
                  Email
                </label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                  <input
                    id="settings-email"
                    type="email"
                    value={user?.email ?? ''}
                    readOnly
                    className="w-full rounded-md border border-gray-700 bg-gray-800 py-3 pl-10 pr-4 text-gray-300"
                  />
                </div>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label htmlFor="current-password" className="mb-1 block text-sm font-medium text-gray-300">
                    Current password
                  </label>
                  <input
                    id="current-password"
                    type="password"
                    value={currentPassword}
                    onChange={(event) => setCurrentPassword(event.target.value)}
                    autoComplete="current-password"
                    className="w-full rounded-md border border-gray-700 bg-gray-800 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Enter your current password"
                  />
                </div>

                <div>
                  <label htmlFor="new-password" className="mb-1 block text-sm font-medium text-gray-300">
                    New password
                  </label>
                  <input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    autoComplete="new-password"
                    className="w-full rounded-md border border-gray-700 bg-gray-800 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="At least 8 characters"
                  />
                </div>

                <div>
                  <label htmlFor="confirm-new-password" className="mb-1 block text-sm font-medium text-gray-300">
                    Confirm new password
                  </label>
                  <input
                    id="confirm-new-password"
                    type="password"
                    value={confirmNewPassword}
                    onChange={(event) => setConfirmNewPassword(event.target.value)}
                    autoComplete="new-password"
                    className="w-full rounded-md border border-gray-700 bg-gray-800 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Re-enter your new password"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-3 border-t border-gray-800 pt-6 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-gray-500">
                  If you do not remember your current password, use the reset flow from the login page.
                </p>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center rounded-md bg-purple-600 px-5 py-3 font-medium text-white transition-colors duration-300 hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-purple-800"
                >
                  {isSubmitting ? 'Updating password...' : 'Update password'}
                </button>
              </div>
            </form>
          </div>

          <div className="mt-8 rounded-2xl border border-gray-800 bg-gray-900/70 p-6 md:p-8 shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-500/10">
                <Send className="w-6 h-6 text-sky-400" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-white">Telegram</h2>
                <p className="text-sm text-gray-400">
                  Link your Telegram account, choose whether notifications stay enabled, and disconnect the channel separately when needed.
                </p>
              </div>
            </div>

            {telegramErrorMessage && (
              <div className="mb-4">
                <AlertMessage variant="error">{telegramErrorMessage}</AlertMessage>
              </div>
            )}

            {telegramSuccessMessage && (
              <div className="mb-4">
                <AlertMessage variant="success">{telegramSuccessMessage}</AlertMessage>
              </div>
            )}

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_280px] xl:items-start">
              <div className="space-y-4">
                <div className="rounded-xl border border-gray-700 bg-gray-900 p-5">
                  <p className="text-sm font-medium text-gray-200">Channel status</p>

                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold ${telegramStatusTone}`}>
                      {telegramStatus}
                    </span>
                  </div>

                  <div className="mt-4 grid gap-3 lg:grid-cols-2">
                    <div className="rounded-lg border border-gray-700 bg-gray-800/60 px-4 py-3">
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Telegram account</p>
                      <p className="mt-1 text-sm font-medium text-white">
                        {telegram.username ? `@${telegram.username}` : 'No Telegram account linked yet.'}
                      </p>
                    </div>
                    <div className="rounded-lg border border-gray-700 bg-gray-800/60 px-4 py-3">
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Connection info</p>
                      <p className="mt-1 text-sm font-medium text-white">
                        {linkedAtLabel ? `Linked on ${linkedAtLabel}` : 'Link this channel to start receiving alerts.'}
                      </p>
                    </div>
                  </div>

                  {linkExpiresAt && !telegram.linked && telegramStatus === 'Linking in progress' && (
                    <p className="mt-2 text-sm text-sky-300">
                      Waiting for confirmation from Telegram until {formatLinkedAt(linkExpiresAt) ?? linkExpiresAt}.
                    </p>
                  )}
                </div>

                <div className="rounded-xl border border-gray-700 bg-gray-900 p-5">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="max-w-xl">
                      <p className="text-sm font-medium text-gray-200">Receive Telegram notifications</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={telegram.linked && telegram.enabled}
                      aria-label="Receive Telegram notifications"
                      disabled={telegramToggleDisabled}
                      onClick={() => void handleToggleTelegramNotifications(!telegram.enabled)}
                      className={`relative inline-flex h-7 w-14 shrink-0 self-start sm:mt-1 sm:self-auto items-center rounded-full transition-colors ${
                        telegram.linked && telegram.enabled ? 'bg-purple-600' : 'bg-gray-700'
                      } ${telegramToggleDisabled ? 'cursor-not-allowed opacity-60' : ''}`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                          telegram.linked && telegram.enabled ? 'translate-x-8' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3 xl:min-w-[280px]">
                {!telegram.linked ? (
                  <button
                    type="button"
                    onClick={() => void handleConnectTelegram()}
                    disabled={isTelegramBusy || !isTelegramServiceConfigured}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-purple-600 px-5 py-3 font-medium text-white transition-colors duration-300 hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-purple-800"
                  >
                    <Send className="h-4 w-4" />
                    {isTelegramBusy
                      ? 'Creating link...'
                      : !isTelegramServiceConfigured
                        ? 'Telegram unavailable'
                        : 'Connect Telegram'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => void handleDisconnectTelegram()}
                    disabled={isTelegramBusy}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-red-500/40 bg-red-500/10 px-5 py-3 font-medium text-red-100 transition-colors duration-300 hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Unplug className="h-4 w-4" />
                    {isTelegramBusy ? 'Disconnecting...' : 'Disconnect Telegram'}
                  </button>
                )}
                <p className="text-sm text-gray-500">
                  The Telegram bot link is generated from your current session. Keep the settings page open until the status changes to Connected.
                </p>
                {pendingTelegramBotUrl && (
                  <a
                    href={pendingTelegramBotUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center rounded-md border border-sky-400/40 bg-sky-500/10 px-4 py-2 text-sm font-medium text-sky-100 hover:bg-sky-500/20"
                  >
                    Open Telegram bot manually
                  </a>
                )}
                {!isTelegramServiceConfigured && (
                  <p className="text-sm text-amber-300">
                    Telegram bot is not configured in this environment. Set TELEGRAM_BOT_TOKEN and TELEGRAM_BOT_USERNAME in compose env files.
                  </p>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SettingsPage;
